import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { isSameCalendarDay } from '../utils/commerceDisplay';
import CommerceDateInput, { dateToInputValue } from './CommerceDateInput';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function startOfGrid(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const dow = first.getDay();
  const offset = dow === 0 ? 6 : dow - 1;
  const start = new Date(year, monthIndex, 1 - offset);
  start.setHours(0, 0, 0, 0);
  return start;
}

function stripTime(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export default function CommerceMonthCalendar({
  value,
  onChange,
  minDate,
  accentColor = '#0f766e',
  isDayDisabled,
  className = '',
  size = 'default',
}) {
  const large = size === 'large';
  const min = minDate ? stripTime(minDate) : stripTime(new Date());
  const initial = value ? new Date(value) : min;
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  useEffect(() => {
    if (!value) return;
    const d = stripTime(value);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }, [value == null ? null : value.getTime()]);

  const cells = useMemo(() => {
    const start = startOfGrid(viewYear, viewMonth);
    const list = [];
    for (let i = 0; i < 42; i += 1) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const inMonth = date.getMonth() === viewMonth;
      const day = stripTime(date);
      const extraOff = typeof isDayDisabled === 'function' ? isDayDisabled(day) : false;
      const disabled = !inMonth || day < min || extraOff;
      list.push({ date: day, inMonth, disabled, key: day.toISOString() });
    }
    return list;
  }, [viewYear, viewMonth, min, isDayDisabled]);

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const today = stripTime(new Date());
  const selected = value ? stripTime(value) : null;

  return (
    <div
      className={`commerce-month-calendar rounded-2xl border border-stone-200/80 bg-white shadow-sm overflow-hidden ${
        large ? 'commerce-month-calendar--large' : ''
      } ${className}`.trim()}
    >
      <div
        className="commerce-month-calendar__head flex items-center justify-between gap-2 px-4 py-3 border-b border-stone-100"
        style={{ backgroundColor: `${accentColor}12` }}
      >
        <button
          type="button"
          className="p-2 rounded-lg hover:bg-white/80 transition-colors"
          onClick={() => shiftMonth(-1)}
          aria-label="Mois précédent"
        >
          <Icon name="ChevronLeft" size={18} />
        </button>
        <p className="commerce-month-calendar__title text-sm font-semibold capitalize text-stone-800">
          {monthLabel}
        </p>
        <button
          type="button"
          className="p-2 rounded-lg hover:bg-white/80 transition-colors"
          onClick={() => shiftMonth(1)}
          aria-label="Mois suivant"
        >
          <Icon name="ChevronRight" size={18} />
        </button>
      </div>

      <div className="commerce-month-calendar__body p-3 sm:p-4">
        <label className="commerce-month-calendar__manual block mb-3">
          <span className="sr-only">Saisir une date</span>
          <CommerceDateInput
            className="commerce-month-calendar__date-input w-full min-w-0 rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-300/80"
            value={value}
            min={dateToInputValue(min)}
            onChange={(d) => {
              if (!d) {
                onChange?.(null);
                return;
              }
              if (typeof isDayDisabled === 'function' && isDayDisabled(stripTime(d))) return;
              if (stripTime(d) < min) return;
              onChange?.(stripTime(d));
            }}
          />
        </label>
        <div className="commerce-month-calendar__weekdays grid grid-cols-7 gap-1 mb-2 text-center text-[10px] font-semibold uppercase tracking-wide text-stone-500">
          {WEEKDAYS.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        <div className="commerce-month-calendar__days grid grid-cols-7 gap-1">
          {cells.map(({ date, inMonth, disabled, key }) => {
            const isToday = isSameCalendarDay(date, today);
            const isSelected = selected && isSameCalendarDay(date, selected);
            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                onClick={() => !disabled && onChange?.(new Date(date))}
                className={`commerce-month-calendar__day aspect-square text-sm font-medium transition-all ${
                  large ? 'min-h-[44px] max-h-none rounded-none' : 'max-h-11 rounded-xl'
                } ${
                  disabled
                    ? 'text-stone-400 bg-stone-100 cursor-not-allowed opacity-60'
                    : isSelected
                      ? 'text-white shadow-md scale-[1.02]'
                      : isToday
                        ? 'ring-2 ring-offset-1 text-stone-900 hover:bg-stone-100'
                        : 'text-stone-800 hover:bg-stone-100'
                }`}
                style={
                  isSelected
                    ? { backgroundColor: accentColor, ringColor: accentColor }
                    : isToday
                      ? { ringColor: accentColor }
                      : undefined
                }
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
